import React, { useState, useEffect, useRef } from 'react';
import styles from './Changelog.module.scss';
import loadingGif from '../../../Assets/Animations/loading.gif';
import ExpandableText from './LocalComponents/ExpandableText';
import { userApi } from '../../../Utils/API';
import useOutsideClick from '../../../hooks/useOutsideClick';
import { formatRelativeDate } from './LocalComponents/Utils/formatDate';
import { GRAPHQL_PROXY } from '../../../Utils/Endpoints';

const ITEMS_PER_LOAD = 5;

const Changelog = () => {
  const [changelogItems, setChangelogItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [visibleItems, setVisibleItems] = useState(ITEMS_PER_LOAD);
  const [scrollLoading, setScrollLoading] = useState(false);
  const loadingRef = useRef(false);
  const filterRef = useRef(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'

  async function getChangelogData() {
    const query = `
      query MyQuery {
        changelogPage {
          list {
            contentItems {
              ... on Changelog {
                displayText
                content {
                  html
                }
                createdUtc
                modifiedUtc
                publishedUtc
                entryType {
                  termContentItems {
                    displayText
                  }
                }
              }
            }
          }
        }
      }
    `;

    const token_type = localStorage.getItem("token_type");
    const access_token = localStorage.getItem("access_token");

    if (!token_type || !access_token) {
      throw new Error("Authorization token not found.");
    }

    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `${token_type} ${access_token}`,
      },
    };

    const body = { query };

    try {
      const resp = await userApi.post(GRAPHQL_PROXY, body, config);
      if (resp.data && resp.data.data && resp.data.data.changelogPage) {
        const processedData = resp.data.data.changelogPage[0].list.contentItems.map(item => {
          const type = item.entryType.termContentItems[0]?.displayText || 'General';
          const title = item.displayText;
          const description = item.content.html;

          return {
            type,
            title,
            description,
            date: formatRelativeDate(item.modifiedUtc),
            absoluteDate: new Date(item.modifiedUtc),
          };
        });
        setChangelogItems(processedData);
      }
    } catch (error) {
      console.error("Error fetching changelog data:", error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    getChangelogData();
  }, []);

  const filterTypes = [...new Set(changelogItems.map((item) => item.type))];

  const filteredData = changelogItems
    .filter((item) => (activeFilters.length ? activeFilters.includes(item.type) : true))
    .sort((a, b) => {
      return sortOrder === 'asc' ? a.absoluteDate - b.absoluteDate : b.absoluteDate - a.absoluteDate;
    });

  useEffect(() => {
    const handleScroll = () => {
      if (loadingRef.current || visibleItems >= filteredData.length) return;

      if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 200) {
        loadingRef.current = true;
        setScrollLoading(true);
        setTimeout(() => {
          setVisibleItems((prev) => prev + ITEMS_PER_LOAD);
          setScrollLoading(false);
          loadingRef.current = false;
        }, 1000);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [visibleItems, filteredData]);

  useOutsideClick(filterRef, () => {
    if (isFilterOpen) {
      setIsFilterOpen(false);
    }
  });

  const handleFilterChange = (type) => {
    setActiveFilters((prevFilters) => {
      if (prevFilters.includes(type)) {
        return prevFilters.filter((f) => f !== type);
      } else {
        return [...prevFilters, type];
      }
    });
  };

  const getIcon = (type) => {
    switch (type) {
      case 'Added':
        return 'add';
      case 'Updated':
        return 'build';
      case 'Removed':
        return 'delete';
      default:
        return 'info';
    }
  };

  const formatTitle = (title) => {
    return title;
  };

  if (isLoading) {
    return (
      <div className={styles.initialLoadingContainer}>
        <img src={loadingGif} alt="Loading..." />
      </div>
    );
  }

  return (
    <div className={styles.mainContent}>
      <div className={styles.changelogPage}>
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Changelog</h1>
          <div className={styles.filterContainer} ref={filterRef}>
            <button className={styles.filterButton} onClick={() => setIsFilterOpen(!isFilterOpen)}>
              <i className="material-icons">filter_list</i>
            </button>
            {isFilterOpen && (
              <div className={styles.filterDropdown}>
                <div className={styles.filterGroup}>
                  <h5 className={styles.filterGroupTitle}>Filter by Type</h5>
                  {filterTypes.map((type) => (
                    <div key={type} className={styles.filterOption}>
                      <input
                        type="checkbox"
                        id={`filter-${type}`}
                        checked={activeFilters.includes(type)}
                        onChange={() => handleFilterChange(type)}
                      />
                      <label htmlFor={`filter-${type}`}>{type}</label>
                    </div>
                  ))}
                </div>
                <div className={styles.filterGroup}>
                  <h5 className={styles.filterGroupTitle}>Sort by Date</h5>
                  <div className={styles.filterOption}>
                    <input
                      type="radio"
                      id="sort-desc"
                      name="sortOrder"
                      checked={sortOrder === 'desc'}
                      onChange={() => setSortOrder('desc')}
                    />
                    <label htmlFor="sort-desc">Newest First</label>
                  </div>
                  <div className={styles.filterOption}>
                    <input
                      type="radio"
                      id="sort-asc"
                      name="sortOrder"
                      checked={sortOrder === 'asc'}
                      onChange={() => setSortOrder('asc')}
                    />
                    <label htmlFor="sort-asc">Oldest First</label>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {filteredData.slice(0, visibleItems).map((item, index) => (
          <div key={index} className={styles.changeItem}>
            <div className={`${styles.tag} ${styles[item.type.toLowerCase()]}`}>
              <i className="material-icons">{getIcon(item.type)}</i>
              <span>{item.type}</span>
            </div>
            <h2 className={styles.title}>{formatTitle(item.title)}</h2>
            <div className={styles.meta}>
              <i className="material-icons">schedule</i>
              <span>{item.date}</span>
            </div>
            <ExpandableText htmlContent={item.description} maxLength={300} />
          </div>
        ))}
        {scrollLoading && (
          <div className={styles.loadingContainer}>
            <img src={loadingGif} alt="Loading..." />
          </div>
        )}
      </div>
    </div>
  );
};

export default Changelog;